import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * Streama CMS · Toast store
 *
 * Singleton queue for transient notifications. The prototype scattered
 * these via `data-toast="..."` attributes on buttons; the new system
 * is store-driven so any code (API interceptor, form handler, store
 * action) can push a toast.
 *
 * Toasts auto-dismiss after a kind-dependent default. The actual UI
 * component (Toast.vue, lands in UI Sprint 1 Step 2) subscribes to
 * the queue and renders.
 */

export type ToastKind = 'info' | 'success' | 'warning' | 'error'

export interface Toast {
  id: number
  kind: ToastKind
  message: string
  /** Optional second-line context (smaller, dimmer). */
  detail?: string
  /** Auto-dismiss timeout in ms. 0 = sticky. Default depends on kind. */
  ttl: number
  createdAt: number
}

export interface ToastInput {
  kind?: ToastKind
  message: string
  detail?: string
  ttl?: number
}

const DEFAULT_TTL: Record<ToastKind, number> = {
  info: 4000,
  success: 3000,
  warning: 6000,
  error: 8000,
}

let nextId = 1

export const useToastStore = defineStore('toast', () => {
  const queue = ref<Toast[]>([])

  function push(input: ToastInput): number {
    const kind = input.kind ?? 'info'
    const id = nextId++
    const toast: Toast = {
      id,
      kind,
      message: input.message,
      detail: input.detail,
      ttl: input.ttl ?? DEFAULT_TTL[kind],
      createdAt: Date.now(),
    }
    queue.value.push(toast)

    if (toast.ttl > 0) {
      window.setTimeout(() => dismiss(id), toast.ttl)
    }
    return id
  }

  function dismiss(id: number): void {
    queue.value = queue.value.filter((t) => t.id !== id)
  }

  function clear(): void {
    queue.value = []
  }

  return {
    queue,
    push,
    dismiss,
    clear,
  }
})
